import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrders } from "../../slice/orderSlice";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { EyeIcon, LayoutList, ShoppingBag } from "lucide-react";
import Header from "../Header";
import { Button, Card } from "@mui/material";
import { LoaderPage } from "../../helpers/Loader";

const OrderHistory = () => {
  const { ordersList, isLoading, totalPage, currentPage, totalCount } =
    useSelector((state) => state.ordersListData);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const history = useNavigate();
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);
  let storedToken = localStorage.getItem("token");
  let usermail;
  if (storedToken) {
    let tokenParts = storedToken.split(".");
    if (tokenParts.length === 3) {
      let decodedPayload = JSON.parse(atob(tokenParts[1]));
      usermail = decodedPayload?.usermail;
    } else {
      console.error("Invalid token format");
    }
  } else {
    console.error("Token not found in localStorage");
  }
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getOrders({ userEmail: usermail }));
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleViewOrder = (orderId) => {
    history(`/orderdetails/${orderId}`);
  };

  const formatDate = (date) => {
    return moment(date).format("DD/MM/YYYY HH:mm");
  };

  const renderPagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 mx-1 rounded ${
            page === i
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };
  const hangleContinueShop = () => {
    history("/shop");
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* <Header /> */}
      {/* Header Banner */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_theme(colors.blue.50),_transparent_40%),radial-gradient(circle_at_bottom_left,_theme(colors.amber.50),_transparent_40%)]" />
          <div className="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-white/80 shadow-xl shadow-indigo-600/10 ring-1 ring-indigo-50" />
        </div>

        <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-4xl">
            <div className="flex justify-center gap-x-6 text-indigo-600/80">
              <ShoppingBag className="h-8 w-8 animate-pulse" />
              <LayoutList className="h-8 w-8" />
            </div>

            <figure className="mt-6">
              <blockquote className="text-center text-xl font-semibold leading-8 text-gray-900 sm:text-2xl sm:leading-9">
                <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">
                  Orders History List
                </h1>
                <div className="mt-4 text-base leading-7 text-gray-600">
                  View and manage your complete order history
                </div>
              </blockquote>
              <div className="mt-6 flex items-center justify-center gap-x-6">
                <div className="h-px w-16 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
                <div className="text-sm leading-6 text-indigo-600">
                  Your Shopping Journey
                </div>
                <div className="h-px w-16 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
              </div>
            </figure>
          </div>
        </div>

        {/* Decorative Elements */}
        <div
          className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl xl:-top-6"
          aria-hidden="true"
        >
          <div className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-10" />
        </div>
      </div>
      {isLoading ? (
        <LoaderPage />
      ) : (
        <>
          {totalCount > 0 ? (
            <>
              {/* Main Content */}
              <div className="container mx-auto px-4 py-8">
                {/* Orders Table */}
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                  {/* {isLoading ? (
                    <LoaderPage />
                  ) : ( */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          {[
                            "S.No.",
                            "Order Date & Time",
                            "Price",
                            "No. Of Items",
                            "Actions",
                          ].map((header) => (
                            <th
                              key={header}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {ordersList?.orders?.map((order, index) => {
                          const totalQuantity =
                            Array.isArray(order.productDetails) &&
                            order.productDetails.length > 0
                              ? order.productDetails.reduce(
                                  (sum, product) =>
                                    sum + (product.quantity || 0),
                                  0
                                )
                              : 0;

                          return (
                            <tr
                              key={order?._id}
                              className="hover:bg-gray-50 transition duration-150 ease-in-out"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                {index + 1}
                              </td>
                              <td className="px-6 py-4 text-blue-600">
                                {formatDate(order?.createdAt)}
                              </td>
                              <td className="px-6 py-4">{order?.amount}</td>
                              <td className="px-6 py-4">{totalQuantity}</td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() => handleViewOrder(order?._id)}
                                  className="text-gray-600 hover:text-blue-600 transition-colors"
                                  title="View Order Details"
                                >
                                  <EyeIcon className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* // )} */}
                </div>

                {/* Pagination */}
                <div className="flex justify-center items-center mt-8 space-x-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                  >
                    Previous
                  </button>

                  {renderPagination()}

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPage}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className=" bg-background flex items-center justify-center p-4">
              <Card className="w-full max-w-md p-8">
                <div className="flex flex-col items-center text-center">
                  <h2 className="text-2xl font-semibold tracking-tight mb-2">
                    No order history found
                  </h2>

                  <p className="text-muted-foreground mb-6">
                    Looks like you haven't placed any orders yet. Start shopping
                    to create your first order.
                  </p>
                  <Button
                    className="text-indigo-600"
                    onClick={hangleContinueShop}
                  >
                    Buy Products
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrderHistory;
